import { useEffect, useState } from "react";
import { EonUiPreloaderSquares } from "@eon-ui/eon-ui-components-react";
import { useJwtToken } from "@me8eon/react_authentication";
import type { ItemDetail, PolicyHygieneRow } from "../aidwops.types";
import useAidwopsTriageStore, { NO_SELECTION } from "../AidwopsTriageStore";
import { api } from "../expertApi";
import { ChoiceButton, TriageNotice, TriageToolbar } from "./TriageParts";
import "./ExpertQueue.scss";

type LoadState =
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "ready"; rows: PolicyHygieneRow[] };

/**
 * A placeholder for a future "stage in ServiceNow" action, mirroring `BatteryDevicesSection`'s real one. The
 * policy clean-up SNOW flow has no endpoint yet, so this only simulates the wait and never calls any API.
 */
type DummySnowState =
    | { status: "idle" }
    | { status: "creating" }
    | { status: "created"; count: number; ignored: number };

const STATUS_LABEL: Record<PolicyHygieneRow["status"], string> = {
    unassigned: "unassigned",
    superseded: "superseded",
    duplicate: "duplicate",
    redundant: "redundant",
};

const keyOf = (row: PolicyHygieneRow, index: number) =>
    row.policy_id ?? `${row.status}-${row.policy_name ?? "unnamed"}-${index}`;

/**
 * The full, named list of Intune policy hygiene findings (unassigned, superseded, duplicate, redundant), read
 * live from `GET /api/intune/policy-hygiene`. Unlike the queue finding's own evidence text -- which only
 * carries per-category counts and a handful of sample names -- this endpoint reshapes the Intune MCP tool's
 * output into one row per policy, so every finding can be listed and triaged, not just the sampled few.
 */
export function PolicyHygieneSection({ item }: { item: ItemDetail }) {
    const jwtToken = useJwtToken();
    const selection =
        useAidwopsTriageStore((state) => state.selections[item.id]) ??
        NO_SELECTION;
    const toggle = useAidwopsTriageStore((state) => state.toggle);
    const setMany = useAidwopsTriageStore((state) => state.setMany);
    const [state, setState] = useState<LoadState>({ status: "loading" });
    const [attempt, setAttempt] = useState(0);
    const [snow, setSnow] = useState<DummySnowState>({ status: "idle" });
    // Policies staged into a (demo) clean-up request: removed from the list so staging one looks final, not
    // just a flag toggle. Keyed by `keyOf`, independent of `selection` so clearing/changing other choices
    // afterwards can't bring a staged policy back.
    const [stagedKeys, setStagedKeys] = useState<Set<string>>(new Set());

    // A new choice is a new request: the demo result shown belonged to the old one.
    useEffect(() => {
        setSnow((current) =>
            current.status === "creating" ? current : { status: "idle" }
        );
    }, [selection]);

    useEffect(() => {
        let isCurrent = true;
        setState({ status: "loading" });

        void (async () => {
            try {
                const accessToken = await jwtToken();
                if (!accessToken) {
                    throw new Error("Unable to acquire access token for expert API");
                }
                const hygiene = await api.getPolicyHygiene(accessToken);
                if (isCurrent) {
                    setState({ status: "ready", rows: hygiene.rows });
                }
            } catch (error) {
                if (isCurrent) {
                    setState({
                        status: "error",
                        message:
                            error instanceof Error
                                ? error.message
                                : String(error),
                    });
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
        // jwtToken follows useJwtToken's unstable function identity; a reload is what `attempt` is for.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attempt]);

    const label = (
        <div className="expert-queue-item-detail-card-section-label">
            Policy clean-up triage
        </div>
    );

    if (state.status === "loading") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <div className="triage-loading">
                    <EonUiPreloaderSquares />
                    <p className="expert-queue-item-detail-card-section-text">
                        Loading the policies...
                    </p>
                </div>
            </div>
        );
    }

    if (state.status === "error") {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <div className="ac-alert" role="alert">
                    <p className="ac-alert-title">
                        Couldn't load the policies.
                    </p>
                    <p className="ac-alert-detail">{state.message}</p>
                </div>
                <div>
                    <button
                        type="button"
                        className="triage-button"
                        onClick={() => setAttempt((count) => count + 1)}
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    const { rows: rawRows } = state;
    if (rawRows.length === 0) {
        return (
            <div className="expert-queue-item-detail-card-section">
                {label}
                <p className="expert-queue-item-detail-card-section-text">
                    No unassigned, superseded, duplicate or redundant policies were found.
                </p>
            </div>
        );
    }

    const rawKeys = rawRows.map(keyOf);
    const visible = rawRows
        .map((row, index) => ({ row, key: rawKeys[index] }))
        .filter(({ key }) => !stagedKeys.has(key));
    const rows = visible.map((entry) => entry.row);
    const keys = visible.map((entry) => entry.key);
    const cleanup = keys.filter((key) => selection[key] === "cleanup").length;
    const ignored = keys.filter((key) => selection[key] === "ignore").length;

    // Dummy: there is no clean-up SNOW endpoint yet, so this only simulates the wait and never calls any API.
    const stageSnowCleanup = () => {
        const cleanupKeys = keys.filter((key) => selection[key] === "cleanup");
        setSnow({ status: "creating" });
        window.setTimeout(() => {
            setSnow({ status: "created", count: cleanup, ignored });
            setStagedKeys((current) => new Set([...current, ...cleanupKeys]));
        }, 300);
    };

    return (
        <div className="expert-queue-item-detail-card-section">
            {label}
            <p className="expert-queue-item-detail-card-section-text">
                {rows.length} {rows.length === 1 ? "policy" : "policies"} named below, read live from the
                Intune policy hygiene index.
            </p>

            <TriageToolbar
                counts={[
                    { label: "clean up", value: cleanup },
                    { label: "ignored", value: ignored },
                    { label: "not reviewed", value: keys.length - cleanup - ignored },
                ]}
                actions={[
                    {
                        label: "Clean up all",
                        onClick: () => setMany(item.id, keys, "cleanup"),
                    },
                    {
                        label: "Ignore all",
                        onClick: () => setMany(item.id, keys, "ignore"),
                    },
                    {
                        label: "Reset",
                        onClick: () => setMany(item.id, keys, null),
                    },
                ]}
            />

            {rows.length > 0 ? (
                <ul className="triage-list">
                    {rows.map((row, index) => {
                        const key = keys[index];
                        const choice = selection[key];
                        const name = row.policy_name ?? row.policy_id ?? "Unknown policy";
                        return (
                            <li
                                key={key}
                                className="triage-row"
                                data-choice={choice ?? ""}
                            >
                                <div className="triage-row-top">
                                    <div className="triage-name">
                                        <strong>{name}</strong>
                                        {row.policy_type && (
                                            <span className="triage-sub">
                                                {row.policy_type}
                                            </span>
                                        )}
                                    </div>
                                    <span
                                        className={`expert-queue-view-tag expert-queue-view-tag--${row.status === "unassigned" ? "high" : "medium"}`}
                                    >
                                        {STATUS_LABEL[row.status]}
                                    </span>
                                </div>
                                {(row.recommendation || row.related_policy) && (
                                    <p className="expert-queue-item-detail-card-section-text">
                                        {row.recommendation}
                                        {row.related_policy
                                            ? `${row.recommendation ? " " : ""}Related policy: ${row.related_policy}.`
                                            : ""}
                                    </p>
                                )}
                                <div className="triage-actions">
                                    <ChoiceButton
                                        label="Clean up"
                                        tone="act"
                                        pressed={choice === "cleanup"}
                                        onClick={() => toggle(item.id, key, "cleanup")}
                                    />
                                    <ChoiceButton
                                        label="Ignore"
                                        tone="ignore"
                                        pressed={choice === "ignore"}
                                        onClick={() => toggle(item.id, key, "ignore")}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="expert-queue-item-detail-card-section-text">
                    All flagged policies have been staged for clean-up.
                </p>
            )}

            <div className="triage-snow-queue">
                <div>
                    <strong>
                        {cleanup} {cleanup === 1 ? "policy" : "policies"} staged for
                        {" "}Clean-up Request
                    </strong>
                    <div className="triage-snow-queue-note">
                        {ignored} ignored · demo only, nothing is sent
                    </div>
                </div>
                <button
                    type="button"
                    className="triage-snow-button"
                    disabled={
                        cleanup === 0 ||
                        snow.status === "creating" ||
                        snow.status === "created"
                    }
                    onClick={stageSnowCleanup}
                >
                    {snow.status === "creating" ? (
                        <EonUiPreloaderSquares />
                    ) : (
                        <svg
                            aria-hidden="true"
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path
                                d="M3 3.5h10v9H3zM5 6.5h6M5 9.5h3"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    )}
                    Stage Clean-up ({cleanup})
                </button>
            </div>

            {snow.status === "creating" && (
                <div className="triage-snow-result" role="status">
                    Staging the clean-up request...
                </div>
            )}
            {snow.status === "created" && (
                <div className="triage-snow-result" role="status">
                    Clean-up staged for {snow.count}{" "}
                    {snow.count === 1 ? "policy" : "policies"} ({snow.ignored}{" "}
                    ignored).
                </div>
            )}

            <TriageNotice what="A clean-up marks the policy for retirement by its owners." />
        </div>
    );
}

export default PolicyHygieneSection;
